//@ts-nocheck
import React, { Fragment, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { scrollUpPage, updateProductGridColumns } from "src/_utils/elements";
import { CSS } from "src/_utils/styles";
import { getQueryParams } from "src/_utils/urls";
import { Btn, H6, Image, LI, P, UL } from "src/AbstractElements";
import { EBSSSPagination } from "src/base/atoms/EBSSSPagination";
import { useEBSSS } from "src/base/context/EBSSSContext/useEBSSS";
import { useEBSSSLayout } from "src/base/context/EBSSSLayoutContext/useEBSSSLayout";
import Page from "src/base/Page";
import { remotePath } from "src/base/settings/Paths";
import { Category, SkeletonLoader } from "../Products/ProductsCommon";
import { Card, CardBody, CardHeader, Col, Container, Form, Input, InputGroup, InputGroupText, Label, Modal, ModalHeader, Row } from "reactstrap";
import { ChevronDown } from "react-feather";
import { SellersFeatures, SellersGrid } from "./SellersCommon";
import { useSelector } from "react-redux";
import { Layout } from "src/base/store/layoutSlice";
import { ReduxDataKey } from "src/base/store/store";
import EBSSSModal from "src/base/atoms/EBSSSModal";
import { DirectionsRenderer, DirectionsService, GoogleMap } from "@react-google-maps/api";
import { useJsApiLoader } from "@react-google-maps/api";
import { Marker, OverlayView } from "@react-google-maps/api";
import * as PropTypes from "prop-types";
import { memo, useCallback, useMemo, CSSProperties } from "react";

const CustomMarkerWithLabel = ({ address, sellers, onClick }) => {
  const seller = sellers.find((s) => s.id === address?.company_id);
  const position = { lat: Number(address.latitude), lng: Number(address.longitude) };

  return (
    <>
      {/* Marker */}
      <Marker position={position} onClick={() => onClick(address)} title={seller?.fullName} />

      {/* Custom Label */}
      <OverlayView
        position={position}
        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET} // Ensures it's clickable
      >
        <div
          onClick={() => onClick(address)} // Add your onClick handler here
          style={{
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            padding: "5px 10px",
            fontSize: "12px",
            cursor: "pointer",
            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.3)",
            width: "auto", // Allow it to adjust based on content
            minWidth: "150px", // Set a minimum width
            maxWidth: "300px", // Set a maximum width
            marginLeft: 20, // Position adjustments
            marginTop: -40,
            wordBreak: "break-word", // Ensure long text breaks appropriately
            display: "flex",
            alignItems: "center",
          }}
        >
          <div className="d-flex flex-row gap-2 w-100 align-items-center">
            {seller?.profile?.avatar ? (
              <Image
                attrImage={{
                  src: remotePath.assetsUrl(seller?.profile?.avatar),
                  width: 40,
                  alt: seller?.fullName,
                }}
              />
            ) : null}
            <span style={{ flexGrow: 1 }}>{seller?.fullName}</span>
          </div>
        </div>
      </OverlayView>
    </>
  );
};

const MapComponentPropTypes = {
  styles: PropTypes.shape({
    container: PropTypes.object.isRequired,
  }).isRequired,
};

const center: google.maps.LatLngLiteral = {
  lat: 40,
  lng: -8.23,
};

interface Props {
  onClick: (a) => void;
  apiKey: string;
  sellers: any[];
  addresses: { lat: number; lng: number }[]; // Updated to explicitly expect lat/lng objects
  styles: {
    container: CSSProperties | undefined;
  };
}

function MapComponent({ styles, addresses, sellers, apiKey, onClick }: Props): JSX.Element {
  const onMapClick = useCallback((e: google.maps.MapMouseEvent) => {
    console.log("onClick args: ", e);
  }, []);

  const loaderOptions = useMemo(() => {
    if (!apiKey) return null;
    return {
      googleMapsApiKey: apiKey,
      libraries: ["maps"],
      language: "en",
      region: "US",
    };
  }, [apiKey]);

  const { isLoaded, loadError } = useJsApiLoader(loaderOptions || {});

  if (!apiKey) {
    return <div>Loading API Key...</div>;
  }

  if (loadError) {
    return <div>Error loading Google Maps: {loadError?.message ?? ""}</div>;
  }

  if (!isLoaded) {
    return <div>Loading Google Maps...</div>;
  }

  return (
    <div className="map">
      <div className="map-container">
        <GoogleMap id="maps-sellers" mapContainerStyle={styles.container} zoom={7} center={center} onClick={onMapClick}>
          {isLoaded && addresses && addresses?.length ? (
            addresses?.map((address, index) => (
              <CustomMarkerWithLabel
                key={index}
                sellers={sellers}
                onClick={() => onClick(address)}
                address={address}
                position={{ lat: Number(address.latitude), lng: Number(address.longitude) }}
                title={sellers?.find((s) => s.id === address?.company_id)["fullName"]}
                label={{ text: sellers?.find((s) => s.id === address?.company_id)["fullName"], className: "bg-light rounded-2 text-black p-2 mb-3" }}
              />
            ))
          ) : (
            <></>
          )}
        </GoogleMap>
      </div>
    </div>
  );
}

MapComponent.propTypes = MapComponentPropTypes;

memo(MapComponent);

const SellersMapPage = () => {
  const { trans } = useEBSSSLayout();
  const { sellersAddressList, fetchSellersAddressList } = useEBSSS();
  const [modal, setModal] = useState(false);
  const ebsssLayout: Layout = useSelector((state) => state[ReduxDataKey.LAYOUT]);

  const toggle = (addr) => {
    if (modal) {
      setModal(false);
    } else {
      setModal(addr);
    }
  };

  useEffect(() => {
    scrollUpPage();
    fetchSellersAddressList();
  }, []);

  return (
    <Page breads={[{ name: trans("website.sellers-locations"), active: true }]}>
      <Fragment>
        <Container fluid={true} className="product-wrapper">
          <MapComponent
            addresses={sellersAddressList?.addresses}
            sellers={sellersAddressList?.sellers}
            styles={{ container: { width: "100%", height: 800 } }}
            apiKey={ebsssLayout?.commonAppSettings["google-maps-key"]}
            onClick={(addr) => setModal(addr)}
          />
          <EBSSSModal modalTitle={trans("website.location")} modal={modal} toggle={() => toggle(modal)} hideFooter>
            {modal ? (
              <div className="row mx-3 mt-2">
                <div className="d-flex flex-column col-8">
                  <h4 className="mb-3">{sellersAddressList?.sellers?.find((s) => s.id === modal?.company_id)!["fullName"]}</h4>
                  <p>
                    {modal?.address_line_1}, {modal?.address_line_2}, {modal?.locality}
                  </p>
                  <p>
                    {modal?.location}, {modal?.postal_code}
                  </p>
                </div>
                <div className="col-4 d-flex align-items-center">
                  <Link
                    className="shadow-sm text-light border-2 rounded-2 w-auto d-flex text-center py-2 px-2 bg-primary"
                    to={process.env.PUBLIC_URL + "/crm/seller/" + sellersAddressList?.sellers?.find((s) => s.id === modal?.company_id)!["username"]}
                  >
                    {trans("common.go") + " "}
                    {sellersAddressList?.sellers?.find((s) => s.id === modal?.company_id)!["fullName"]}
                  </Link>
                </div>
              </div>
            ) : (
              <></>
            )}
          </EBSSSModal>
        </Container>
      </Fragment>
    </Page>
  );
};

export default SellersMapPage;
